import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { InmateDetailsTableContainer, TableHeader } from "../InmateDetails/styles";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import Modal from "@/common/elements/Modal";
import { Form, Popconfirm } from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import { isNonEmptyVariable } from "@/util/common_function";
import { v4 as uuidv4 } from "uuid";

const ReleaseDetails = ({ data }) => {
  const [releaseNotesForm] = Form.useForm();
  const [QForm] = Form.useForm();

  const [releaseNotesList, setReleaseNotesList] = useState([]);
  const [selectedReleaseNoteId, setSelectedReleaseNoteId] = useState(null);
  const [selectedReleaseNote, setSelectedReleaseNote] = useState(null);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isQFormBtnEnable, setIsQFormBtnEnable] = useState(true);

  // ── helpers ──────────────────────────────────────────────────────────────

  const openAddModal = (editData = null) => {
    setSelectedReleaseNoteId(editData?.releaseNoteID || editData?.id || null);
    releaseNotesForm.setFieldsValue(
      editData
        ? {
            releaseNote: editData.releaseNote ?? "",
            comments: editData.comments ?? "",
            cleared: editData.cleared === "Yes" || editData.cleared === true,
            clearanceNotes: editData.clearanceNotes ?? "",
            clearanceDateTime: editData.clearanceDate
              ? dayjs(`${editData.clearanceDate} ${editData.clearanceTime ?? ""}`)
              : null,
            clearedByStaffID: editData.clearedByStaffID ?? "",
          }
        : {
            releaseNote: "",
            comments: "",
            cleared: false,
            clearanceNotes: "",
            clearanceDateTime: null,
            clearedByStaffID: "",
          }
    );
    setSelectedReleaseNote(editData);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedReleaseNote(null);
    setSelectedReleaseNoteId(null);
  };

  // ── actions ───────────────────────────────────────────────────────────────

  const saveReleaseNote = () => {
    releaseNotesForm
      .validateFields()
      .then((values) => {
        const clearanceDateTime = isNonEmptyVariable(values?.clearanceDateTime)
          ? dayjs(values.clearanceDateTime).format("MM/DD/YYYY HH:mm")
          : null;

        const entry = {
          ...values,
          clearanceDate: clearanceDateTime?.split(" ")[0] ?? null,
          clearanceTime: clearanceDateTime?.split(" ")[1] ?? null,
          clearanceDateTime: undefined,
        };

        if (selectedReleaseNoteId) {
          setReleaseNotesList((prev) =>
            prev.map((p) =>
              p.id === selectedReleaseNoteId || p.releaseNoteID === selectedReleaseNoteId
                ? { ...entry, id: selectedReleaseNoteId }
                : p
            )
          );
        } else {
          setReleaseNotesList((prev) => [...prev, { ...entry, id: uuidv4() }]);
        }
        closeAddModal();
      })
      .catch((err) => console.error(err));
  };

  const qAddSave = () => {
    QForm.validateFields()
      .then((values) => {
        setReleaseNotesList((prev) => [...prev, { ...values, id: uuidv4() }]);
        QForm.resetFields();
        setIsQFormBtnEnable(true);
      })
      .catch((err) => console.error(err));
  };

  const deleteReleaseNote = () => {
    setReleaseNotesList((prev) =>
      prev.filter(
        (item) => item.id !== selectedReleaseNoteId && item.releaseNoteID !== selectedReleaseNoteId
      )
    );
    setSelectedReleaseNoteId(null);
    setSelectedReleaseNote(null);
    setIsRowSelected(false);
  };

  const onRowClick = (rowData) => {
    if (isNonEmptyVariable(rowData)) {
      setSelectedReleaseNoteId(rowData?.releaseNoteID || rowData?.id);
      setSelectedReleaseNote(rowData);
      setIsRowSelected(true);
    }
  };

  // ── read-only iris values ─────────────────────────────────────────────────

  const releaseInfoValues = {
    scheduledReleaseDateTime: null,                                           // not in iris API
    releasedDateTime: data?.dateReleased ? dayjs(data.dateReleased) : null,
    irisReleasedDateTime: data?.irisReleasedDate ? dayjs(data.irisReleasedDate) : null,
    releasedType: data?.releasedType ?? "",
    releasedTo: data?.releasedTo ?? "",
    endOfSentenceDate: data?.endOfSentenceDate ? dayjs(data.endOfSentenceDate) : null,
    totalDaysInCustody: "",                                                   // not in iris API
    releaseWarCheckDate: null,                                                // not in iris API
  };

  return (
    <>
      {/* ── Release Information ── */}
      <Form key={data?.transactionId} initialValues={releaseInfoValues} disabled>
        <StyledSectionHeading marginTop="-10px" marginBottom="0px">
          <span>Release Information</span>
        </StyledSectionHeading>

        <StyledGridRow justify="end">
          <StyledGridCol>
            <FormItem
              label="Override release date?"
              layout="horizontal"
              labelPostion="label-right"
            >
              <Checkbox disabled />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>

        <StyledGridRow gutter={16}>
          <StyledGridCol lg={8}>
            <FormItem label="Scheduled Release Date And Time" name="scheduledReleaseDateTime">
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                placeholder="Select Date and Time"
                disabled
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Release Date And Time" name="releasedDateTime">
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                placeholder="Select Date and Time"
                disabled
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="IRIS Released Date And Time" name="irisReleasedDateTime">
              <CommonDatePicker
                format="MM/DD/YYYY HH:mm"
                placeholder="Select Date and Time"
                disabled
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Release Type" name="releasedType">
              <TextField readOnly />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Released To" name="releasedTo">
              <TextField readOnly />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="End of Sentence" name="endOfSentenceDate">
              <CommonDatePicker format="MM/DD/YYYY HH:mm" disabled />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Total Days in Custody" name="totalDaysInCustody">
              <TextField disabled />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Release War Check Date" name="releaseWarCheckDate">
              <CommonDatePicker format="MM/DD/YYYY" placeholder="MM/DD/YYYY" showTime={false} disabled />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>

      {/* ── Release Notes ── */}
      <StyledSectionHeading>
        <span>Release Notes</span>
      </StyledSectionHeading>

      <Form
        form={QForm}
        initialValues={{ releaseNote: "", comments: "" }}
        onFieldsChange={() => {
          setIsQFormBtnEnable(!QForm.getFieldValue("releaseNote")?.length);
        }}
      >
        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem label="Release Note" name="releaseNote">
              <TextField />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem label="Comments" name="comments">
              <TextField />
            </FormItem>
          </StyledGridCol>
          {/* <StyledGridCol>
            <StyledFlex align="center" style={{ height: "100%", paddingTop: "10px" }}>
              <Button
                color="primary"
                type="primary"
                onClick={qAddSave}
                disabled={isQFormBtnEnable}
              >
                Q-ADD
              </Button>
            </StyledFlex>
          </StyledGridCol> */}
        </StyledGridRow>
      </Form>

      <InmateDetailsTableContainer>
        <TableHeader>
          <div />
          <div>
            {/* <Button color="primary" type="primary" onClick={() => openAddModal()}>
              Add
            </Button> */}
            <Button
              color="primary"
              type="primary"
              style={{ marginLeft: "15px" }}
              disabled={!isRowSelected}
              onClick={() => openAddModal(selectedReleaseNote)}
            >
              Details
            </Button>
            {/* <Popconfirm
              title="Delete the Release Note"
              description="Are you sure you want to delete this release note?"
              onConfirm={deleteReleaseNote}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                style={{ marginLeft: "10px" }}
                disabled={!isRowSelected || releaseNotesList.length === 0}
              >
                Delete
              </Button>
            </Popconfirm> */}
          </div>
        </TableHeader>

        <Table
          headers={[
            { key: "releaseNote", text: "Release Note" },
            { key: "cleared",    text: "Cleared"       },
            { key: "comments",   text: "Comments"      },
          ]}
          data={releaseNotesList.map((item) => ({
            ...item,
            cleared: item.cleared ? "Yes" : "No",
          }))}
          onRowClick={onRowClick}
          selected={!isRowSelected}
          noDataMessage="No release notes found."
        />
      </InmateDetailsTableContainer>

      {/* ── Add / Edit Release Note Modal ── */}
      <Modal
        header="Add Release Note"
        show={showAddModal}
        onHide={closeAddModal}
        footerActions={[{ key: "save", text: "Save", onClick: saveReleaseNote }]}
        footerCloseAction={closeAddModal}
        footerCloseActionText="Cancel"
      >
        <Form form={releaseNotesForm}>
          <StyledGridRow>
            <StyledGridCol span={24}>
              <FormItem
                label="Release Note"
                name="releaseNote"
                rules={[{ required: true, message: "Release Note is required" }]}
              >
                <TextField />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={24}>
              <FormItem label="Comment" name="comments">
                <TextField inputType="textarea" />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledSectionHeading>
            <span>Clearance Information</span>
          </StyledSectionHeading>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.cleared !== curr.cleared}>
            {({ getFieldValue, setFieldsValue }) => {
              const cleared = getFieldValue("cleared");
              return (
                <StyledGridRow gutter={16}>
                  <StyledGridCol span={24}>
                    <FormItem
                      name="cleared"
                      label="Cleared?"
                      valuePropName="checked"
                      layout="horizontal"
                      className="form-item-checkbox-left"
                    >
                      <Checkbox
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFieldsValue({ clearanceDateTime: dayjs() });
                          }
                        }}
                      />
                    </FormItem>
                  </StyledGridCol>

                  {cleared && (
                    <>
                      <StyledGridCol span={12}>
                        <FormItem label="Date / Time" name="clearanceDateTime">
                          <CommonDatePicker format="MM/DD/YYYY HH:mm" />
                        </FormItem>
                      </StyledGridCol>
                      <StyledGridCol span={12}>
                        <FormItem label="Cleared By" name="clearedByStaffID">
                          <TextField />
                        </FormItem>
                      </StyledGridCol>
                      <StyledGridCol span={24}>
                        <FormItem label="Clearance Notes" name="clearanceNotes">
                          <TextField inputType="textarea" />
                        </FormItem>
                      </StyledGridCol>
                    </>
                  )}
                </StyledGridRow>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ReleaseDetails;
